((global) => {
  "use strict";

  const sdk = global.__VOGRACE__ || {};
  const shopify = global.Shopify || {};

  const DEFAULT_CURRENCY = "USD";
  const DEFAULT_RATE = 1;
  const CURRENCY_RATES_API_ENDPOINT = "https://app.vograce.com/shopify-api/currency/rates";
  const CURRENCY_RATES_STORAGE_KEY = "__VOGRACE_CURRENCY_RATES__";

  /**
   * 规范化汇率数据，确保 rate 为数字。
   * @param {Record<string, {name: string, symbol: string, rate: string|number}>} rawData
   * @returns {Record<string, {name: string, symbol: string, rate: number}>}
   */
  function transformCurrencyData(rawData) {
    const registry = {};
    for (const [code, info] of Object.entries(rawData)) {
      registry[code] = {
        name: info.name,
        symbol: info.symbol,
        rate: parseFloat(info.rate) || DEFAULT_RATE,
      };
    }
    return registry;
  }

  /**
   * 拉取最新汇率并写入本地缓存（带时间戳）。
   * @returns {Promise<Record<string, {name: string, symbol: string, rate: number}>|null>}
   */
  async function fetchAndCacheRates() {
    try {
      const response = await global.fetch(CURRENCY_RATES_API_ENDPOINT);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.code !== 200) throw new Error(data.message || "API error");

      const transformed = transformCurrencyData(data.data);
      const payload = {
        timestamp: Date.now(),
        rates: transformed,
      };
      global.localStorage.setItem(CURRENCY_RATES_STORAGE_KEY, JSON.stringify(payload));
      return transformed;
    } catch (error) {
      console.error("[Currency] Fetch failed:", error);
      return null;
    }
  }

  /**
   * UTC 02:10 日切刷新规则：
   * - 当前时间已过今天 UTC 02:10
   * - 且缓存写入时间早于今天 UTC 02:10
   * 满足时返回 true，表示应强制刷新汇率。
   * @param {number} timestamp
   * @param {number} [now=Date.now()]
   * @returns {boolean}
   */
  function shouldRefreshAfterUtc0210(timestamp, now = Date.now()) {
    if (!Number.isFinite(timestamp)) return true;

    const current = new Date(now);
    const utc0210Today = Date.UTC(
      current.getUTCFullYear(),
      current.getUTCMonth(),
      current.getUTCDate(),
      2,
      10,
      0,
      0
    );

    return now >= utc0210Today && timestamp < utc0210Today;
  }

  /**
   * 读取并校验缓存；缓存无效时返回 null。
   * @returns {Record<string, {name: string, symbol: string, rate: number}>|null}
   */
  function getCachedRates() {
    const raw = global.localStorage.getItem(CURRENCY_RATES_STORAGE_KEY);
    if (!raw) return null;
    try {
      const { timestamp, rates } = JSON.parse(raw);
      if (shouldRefreshAfterUtc0210(timestamp)) {
        global.localStorage.removeItem(CURRENCY_RATES_STORAGE_KEY);
        return null;
      }
      return rates;
    } catch {
      return null;
    }
  }

  /**
   * 获取汇率（缓存优先，其次请求，失败时降级）。
   * @returns {Promise<Record<string, {name: string, symbol: string, rate: number}>>}
   */
  async function getRates() {
    const cached = getCachedRates();
    if (cached) return cached;

    const fresh = await fetchAndCacheRates();
    if (fresh) return fresh;

    // 请求失败时，尽量回退到旧缓存。
    const fallback = global.localStorage.getItem(CURRENCY_RATES_STORAGE_KEY);
    if (fallback) {
      try {
        const { rates } = JSON.parse(fallback);
        console.warn("[Currency] Using stale cache due to fetch failure.");
        return rates;
      } catch {
        /* ignore */
      }
    }
    // 最终兜底：仅返回默认币种。
    return { [DEFAULT_CURRENCY]: { name: "US Dollar", symbol: "$", rate: DEFAULT_RATE } };
  }

  class Currency {
    constructor() {
      this._currency = shopify.currency?.active || DEFAULT_CURRENCY;
      this._shopifyRate = parseFloat(shopify.currency?.rate) || DEFAULT_RATE;
      this._rates = {};
      this._ready = false;
      this._initPromise = null;

      // 构造后立即预加载，但不阻塞实例创建。
      this._initPromise = this._init();
    }

    /**
     * 内部初始化：加载汇率并标记 ready。
     * @returns {Promise<void>}
     */
    async _init() {
      if (this._ready) return;
      this._rates = await getRates();
      this._ready = true;
    }

    /**
     * 等待初始化完成。
     * @returns {Promise<Currency>}
     */
    async ready() {
      if (this._initPromise) {
        await this._initPromise;
      }
      return this;
    }

    /**
     * 获取币种汇率；未就绪或缺失时回退到 Shopify 当前汇率。
     * @param {string} currencyCode - 例如 "USD"、"CNY"
     * @returns {number}
     */
    getRate(currencyCode) {
      if (!this._ready) {
        console.warn("[Currency] Rates not ready yet, returning default rate.");
        return this._shopifyRate;
      }
      const info = this._rates[currencyCode];
      return info ? info.rate : this._shopifyRate;
    }

    /**
     * 获取币种完整信息（name/symbol/rate）。
     * @param {string} currencyCode
     * @returns {{name: string, symbol: string, rate: number}|null}
     */
    getCurrencyInfo(currencyCode) {
      if (!this._ready) return null;
      return this._rates[currencyCode] || null;
    }

    /**
     * 获取当前店铺币种信息。
     * @returns {{name: string, symbol: string, rate: number}|null}
     */
    getCurrentCurrencyInfo() {
      return this.getCurrencyInfo(this._currency);
    }

    /**
     * 通用金额转换：from -> to。
     * @param {number|string} amount - 待转换金额
     * @param {string} fromCurrencyCode - 源币种（如 "USD"）
     * @param {string} toCurrencyCode - 目标币种（如 "JPY"）
     * @param {number} precision - 小数位（默认 6）
     * @returns {number}
     */
    convertAmount(amount, fromCurrencyCode = this._currency, toCurrencyCode = DEFAULT_CURRENCY, precision = 6) {
      const numericAmount = Number(amount);
      if (!Number.isFinite(numericAmount)) {
        console.warn("[Currency] Invalid amount for conversion:", amount);
        return 0;
      }

      if (fromCurrencyCode === toCurrencyCode) {
        return numericAmount;
      }

      const fromRate = this.getRate(fromCurrencyCode);
      const toRate = this.getRate(toCurrencyCode);

      if (!Number.isFinite(fromRate) || fromRate <= 0 || !Number.isFinite(toRate) || toRate <= 0) {
        console.warn("[Currency] Invalid rate for conversion:", {
          fromCurrencyCode,
          toCurrencyCode,
          fromRate,
          toRate,
        });
        return numericAmount;
      }

      const converted = (numericAmount / fromRate) * toRate;
      const safePrecision = Number.isInteger(precision) && precision >= 0 ? precision : 6;
      return Number(converted.toFixed(safePrecision));
    }

    /**
     * 当前店铺币种 -> 目标币种。
     * @param {number|string} amount - 待转换金额
     * @param {string} toCurrencyCode - 目标币种（如 "EUR"）
     * @param {number} precision - 小数位（默认 6）
     * @returns {number}
     */
    convertFromCurrent(amount, toCurrencyCode = DEFAULT_CURRENCY, precision = 6) {
      return this.convertAmount(amount, this._currency, toCurrencyCode, precision);
    }

    /**
     * 指定币种 -> 当前店铺币种。
     * @param {number|string} amount - 待转换金额
     * @param {string} fromCurrencyCode - 源币种（如 "GBP"）
     * @param {number} precision - 小数位（默认 6）
     * @returns {number}
     */
    convertToCurrent(amount, fromCurrencyCode = DEFAULT_CURRENCY, precision = 6) {
      return this.convertAmount(amount, fromCurrencyCode, this._currency, precision);
    }

    /**
     * 强制刷新汇率（忽略缓存）。
     * @returns {Promise<Currency>}
     */
    async refresh() {
      const fresh = await fetchAndCacheRates();
      if (fresh) {
        this._rates = fresh;
        this._ready = true;
      }
      return this;
    }
  }

  // 挂载到全局命名空间。
  sdk.currency = new Currency();
})(window);
